import {
  ChangeEventHandler,
  forwardRef,
  Fragment,
  MutableRefObject,
  ReactElement,
  TextareaHTMLAttributes,
  useCallback,
  useRef,
} from 'react';

import styled from 'styled-components';

import { useAriaId } from '@components/component-helpers';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconCollapse } from '@components/icons/collapse';
import { IconExpand } from '@components/icons/expand';
import { Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text/text';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { unasteriskLabel } from '@utils/strings';
import { transitionStandard } from '@utils/transitions';
import { Transient } from '@utils/types';

type BaseProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'disabled'>;

export type TextAreaProps = BaseProps & {
  onChange?(newValue: string): void;
  error?: string;
  label?: string;
  labelIcon?: ReactElement<IconProps>;
  hideLabel?: boolean;
  isDisabled?: boolean;
  dataCy?: string;
  isExpandable?: boolean;
  helpText?: string;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>((props, ref) => {
  const {
    onChange,
    error,
    label,
    labelIcon,
    hideLabel = false,
    isDisabled,
    dataCy,
    children,
    id,
    helpText,
    ...rest
  } = props;
  const onUpdate = useCallback((e) => void onChange?.(e.target.value), [onChange]);
  const showModal = useTextareaExpandedModal();
  const inputRef = useRef(null);
  const inputId = useAriaId('textarea');
  const actualId = id ?? inputId;
  const finalRef = (ref ?? inputRef) as MutableRefObject<HTMLTextAreaElement>;

  const FormLabelContainer = hideLabel ? VisuallyHidden : Fragment;

  return (
    <Stack space={1} style={{ position: 'relative' }}>
      <FormLabelContainer>
        <FormLabel htmlFor={actualId} text={label} icon={labelIcon} />
      </FormLabelContainer>
      <div className="textarea-wrapper">
        {children}
        <StyledTextArea
          id={actualId}
          ref={finalRef}
          onChange={onUpdate}
          spellCheck="false"
          data-error={Boolean(error)}
          data-disabled={isDisabled}
          disabled={isDisabled}
          data-cy={dataCy}
          $isExpandable={props.isExpandable}
          {...rest}
        />
      </div>
      {props.isExpandable && (
        <IconButton
          icon={<IconExpand size={16} />}
          size="sm"
          disabled={isDisabled}
          onClick={() => {
            showModal({
              onChange: onUpdate,
              title: label ?? '',
              defaultValue: finalRef?.current?.value ?? '',
              placeholder: rest.placeholder,
            });
          }}
          style={{ position: 'absolute', right: pxToRem(8), top: pxToRem(hideLabel ? 8 : 36) }}
          variant="ghost-purple"
        />
      )}
      <FormError text={error} />
      {helpText && <Text font="regular14" color="textHelper" text={helpText} />}
    </Stack>
  );
});

const StyledTextArea = styled.textarea<Transient<TextAreaProps, 'isExpandable'>>(
  ({ $isExpandable }) => ({
    borderStyle: 'solid',
    outline: 'none',
    padding: '0.5rem 0.75rem',
    ...($isExpandable && {
      paddingRight: pxToRem(32),
    }),
    transition: transitionStandard('color', 'background', 'border'),
    resize: 'vertical',
    width: '100%',

    borderWidth: theme.form.border.width,
    borderRadius: theme.form.border.radius,
    borderColor: theme.form.border.color,
    backgroundColor: theme.form.bg.color,

    font: theme.font.regular16,
    color: theme.form.value.color,
    minHeight: '7rem',

    '&::placeholder': {
      color: theme.form.placeholder.color,
    },

    '&:not(:disabled)': {
      '&:hover': {
        backgroundColor: theme.form.bg.colorHover,
      },

      '&:focus': {
        backgroundColor: theme.form.bg.colorFocus,
        borderColor: theme.form.border.colorFocus,
        '&::placeholder': {
          color: theme.form.placeholder.colorFocus,
        },
        color: theme.form.value.colorFocus,
      },
    },

    '&[data-error="true"]': {
      borderColor: theme.color.error,
    },
  })
);

export const useTextareaExpandedModal = () => useModal(TextAreaExpandedModal, { variant: 'LARGE' });

export type TextAreaExpandedModalProps = {
  title: string;
  defaultValue: string;
  placeholder: TextAreaProps['placeholder'];
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
};

export const TextAreaExpandedModal = ({ title, ...rest }: TextAreaExpandedModalProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Modal>
      <Modal.SimpleHeader
        title={unasteriskLabel(title)}
        closeButtonProps={{ iconLeft: <IconCollapse />, variant: 'secondary', text: t('close') }}
      />
      <Modal.Content>
        <StyledTextArea {...rest} style={{ minHeight: '50vh', width: '100%', resize: 'none' }} />
      </Modal.Content>
    </Modal>
  );
};

const locale = {
  en: { close: 'Close' },
};
